/* eslint-disable no-shadow */
import { useMsal } from '@azure/msal-react';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import UserBgImg from '../headerNavigation/header/images/profile_1.png';
import { checkImage } from '../util/helperfunctions';
import { useUserData, ClientInfo } from './useUserData';

// Check client's name contain "demo" or "test" or "champion" in any case (case-insensitive),
export const isClientDemo = (name) => {
  const regex = /(demo|test|champion)/i;
  return regex.test(name);
};

interface LogoutOptions {
  redirectTo?: string;
  shouldRedirect?: boolean;
}

export const useAuth = () => {
  const { userData, appendUserData } = useUserData();
  const { instance } = useMsal();
  const { i18n } = useTranslation();
  const [token, setToken] = useState(false);
  const [tokenExpirationDate, setTokenExpirationDate] = useState();
  const [userId, setUserId] = useState(false);
  const [fname, setFname] = useState(false);
  const [access, setAccess] = useState(false);
  const [usageId, setUsageId] = useState<string | boolean | undefined>(false);
  const [image, setImage] = useState<string | boolean | undefined>(false);
  const [tags, setTags] = useState<string[] | boolean | undefined>(false);
  const [clientName, setClientName] = useState('');
  const [npsStatus, setNpsStatus] = useState(false);
  const [authType, setAuthType] = useState(null);
  const [email, setEmail] = useState(null);
  const [language, setLanguage] = useState(false);
  const [isHub, setIsHub] = useState(false);

  const languageHandler = useCallback(async (newLang) => {
    setLanguage(newLang);
    await i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang); // Store the selected language
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (
      uid,
      token,
      fname,
      expirationDate,
      access,
      image,
      tags,
      clientName,
      npsStatus,
      authType,
      email,
      userLanguage,
      hub = false,
      fnfDisabled = false,
      clientInfo: ClientInfo | undefined = undefined
    ) => {
      const isValidImage = await checkImage(`${image}`);

      if (isValidImage) {
        setImage(() =>
          // force cached image to refresh
          image?.startsWith('https://') ? `${image}?cacheBust=${new Date().getTime()}` : image
        );
      } else {
        setImage(UserBgImg);
      }
      // Store data to session state
      setToken(token);
      setUserId(uid);
      setFname(fname);
      setAccess(access);
      setUsageId(usageId);
      setTags(tags);
      setClientName(clientName);
      setNpsStatus(npsStatus);
      setAuthType(authType);
      setEmail(email);
      setLanguage(userLanguage);
      setIsHub(hub);

      const tokenExpirationDate =
        expirationDate || new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 3); // 3 days from loggin in

      setTokenExpirationDate(tokenExpirationDate);

      if (userLanguage) {
        languageHandler(userLanguage);
      }

      if (!isClientDemo(clientName)) {
        // Identify people to Customer IO - TODO Add dept, sub_dept and region
        (window as any)._CIO.identify({
          id: uid,
          email,
          name: fname,
          client: clientName,
          access,
          // department: client.departments[Number(dept) - 1],
          // sub_department: client.sub_departments[Number(dept) - 1][Number(sub_dept) - 1],
          // regionid: regionid,
        });
      }

      // Build user data object for local storage
      const userDataToStore: Record<string, unknown> = {
        userId: uid,
        token,
        expiration: tokenExpirationDate.toISOString(),
        fname,
        access,
        usageId,
        image,
        clientName,
        npsStatus,
        authType,
        email,
        isHub: hub,
      };

      // If clientInfo is provided, include client-specific data to update local storage
      // This handles the case when user logs in with a different clientId than stored
      if (clientInfo) {
        userDataToStore.clientid = clientInfo.clientid;
        userDataToStore.isStandardAuthActive = clientInfo.isStandardAuthActive;
        userDataToStore.isMicrosoftAuthActive = clientInfo.isMicrosoftAuthActive;
        userDataToStore.isGoogleAuthActive = clientInfo.isGoogleAuthActive;
        userDataToStore.singleSignOn = clientInfo.sso;
        userDataToStore.logo = clientInfo.logo;
        userDataToStore.logo_dark = clientInfo.logo_dark;
        userDataToStore.fnfDisabled = clientInfo.fnfDisabled;
        userDataToStore.eapPhoneNumber = clientInfo.eapPhoneNumber;
        userDataToStore.emergencyPhoneNumber = clientInfo.emergencyPhoneNumber;
        userDataToStore.customFeatureClient = clientInfo.customFeatureClient;
        userDataToStore.isSkipHealthAssessment = clientInfo.isSkipHealthAssessment;
        userDataToStore.pamClientCode = clientInfo.pamClientCode;
        userDataToStore.open = clientInfo.open;
      }

      // Store data to local storage
      appendUserData(userDataToStore);
      localStorage.setItem(
        'userTags',
        JSON.stringify({
          tags,
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appendUserData]
  );

  const logout = useCallback((options: LogoutOptions | string = {}) => {
    const { redirectTo = '/', shouldRedirect = true } =
      typeof options === 'string' ? { redirectTo: options, shouldRedirect: true } : options;

    // Remove session from microsoft
    if (authType === 'Microsoft') {
      instance.logoutRedirect();
    }

    if (!isClientDemo(clientName)) {
      // Stop identifying a person for Customer IO
      (window as any)._CIO.reset();
    }

    // Remove stored data to session state
    setToken(null);
    setTokenExpirationDate(null);
    setUserId(null);
    setFname(null);
    setAccess(null);
    setUsageId(null);
    setImage(null);
    setTags(null);
    setClientName('');
    setNpsStatus(null);
    setAuthType(null);
    setEmail(null);
    setLanguage(null);
    setIsHub(false);

    // To clear the whole data stored in localStorage
    localStorage.clear();

    // Remove all saved data from sessionStorage
    sessionStorage.clear();

    // Reset route location only if shouldRedirect is true
    if (shouldRedirect) {
      document.location.href = redirectTo;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileHandler = useCallback(
    async (user) => {
      setFname(user.name);
      setEmail(user.email);
      const isValidImage = await checkImage(`${user.image}`);

      // reload the image if it's valid. NB: the url of the image does not change, but cache needs to be refreshed
      if (user?.image?.startsWith('https://') && isValidImage) {
        setImage(`${user.image}?cacheBust=${new Date().getTime()}`);
      }

      // Set local storage with updated values
      appendUserData({
        fname: user.name,
        email: user.email,
        image: user.image,
      });
    },
    [appendUserData]
  );

  const npsStatusHandler = useCallback(
    (npsStatus) => {
      setNpsStatus(npsStatus);
      appendUserData({
        npsStatus,
      });
    },
    [appendUserData]
  );

  const tagHandler = useCallback((tags) => {
    setTags(tags);
    // alert('taghandler='+tags.length);
    // const tags = JSON.parse(localStorage.getItem('userTags'));
    localStorage.setItem(
      'userTags',
      JSON.stringify({
        tags,
      })
    );
  }, []);

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (userData) {
        const { token, expiration } = userData;

        if (token && expiration) {
          const expirationDate = new Date(expiration); // Convert ISO string to Date object
          const remainingTime = expirationDate.getTime() - new Date().getTime();
          if (remainingTime <= 0) {
            logout();
          }
        }
      }
    };

    const intervalId = setInterval(checkTokenExpiration, 30000); // Check every 30 seconds

    return () => {
      clearInterval(intervalId); // Clear interval on component unmount
    };
  }, [token, logout, tokenExpirationDate, userData]);

  useEffect(() => {
    const storedData = userData;
    const storedTags = JSON.parse(localStorage.getItem('userTags'));
    const storedLanguage = localStorage.getItem('i18nextLng');
    if (storedData && storedData.token && new Date(storedData.expiration) > new Date()) {
      login(
        storedData.userId,
        storedData.token,
        storedData.fname,
        new Date(storedData.expiration),
        storedData.access,
        storedData.image,
        storedTags.tags,
        storedData.clientName,
        storedData.npsStatus,
        storedData.authType,
        storedData.email,
        storedLanguage,
        storedData?.isHub,
        storedData?.fnfDisabled
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  useEffect(() => {
    const storedTags = JSON.parse(localStorage.getItem('userTags'));
    if (storedTags && storedTags.tags) {
      setTags(storedTags.tags);
    }
  }, [tagHandler]);

  return {
    token,
    login,
    logout,
    userId,
    fname,
    tokenExpirationDate,
    access,
    usageId,
    image,
    tags,
    clientName,
    npsStatus,
    authType,
    email,
    language,
    isHub,
    profileHandler,
    npsStatusHandler,
    tagHandler,
    languageHandler,
  };
};
